import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import API_BASE_URL from "../../config/api";
import { formatZoneName, sortZones, ZONE_ORDER, getZoneColor } from "../../utils/zoneUtils";

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Category { id: number; name: string; }

interface HeatmapData {
  categories: Category[];
  zones: string[];
  matrix: Record<string, Record<number, number>>;
}

type HeatmapPeriod = "current-month" | "last-month" | "quarter" | "year" | "all" | "custom";

// ─── Intensity helper ──────────────────────────────────────────────────────────
// count = 0  → 0       (empty — caller handles separately)
// count = 1  → ~0.35  (light fill)
// count = 2  → ~0.60
// count ≥ 3  → scales toward 1.0
const cellOpacity = (count: number, max: number): number => {
  if (!count || !max) return 0;
  return Math.min(1, Math.max(0.35, Math.sqrt(count / max)));
};

// ─── Tooltip state ─────────────────────────────────────────────────────────────

interface TooltipState {
  x: number;
  y: number;
  zone: string;
  cat: string;
  count: number;
  prevCount?: number;
  isCompare?: boolean;
}

// ─── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  studentId: number | string;
  primaryPeriod: HeatmapPeriod;
  comparePeriod?: HeatmapPeriod;
  showCompare?: boolean;
  onCellClick?: (zone: string, categoryId: number) => void;
  primaryStartDate?: string;
  primaryEndDate?: string;
  compareStartDate?: string;
  compareEndDate?: string;
}

// ─── Component ─────────────────────────────────────────────────────────────────

const CategoryZoneGrid: React.FC<Props> = ({
  studentId, primaryPeriod, comparePeriod, showCompare, onCellClick,
  primaryStartDate, primaryEndDate, compareStartDate, compareEndDate
}) => {
  const [data,        setData]        = useState<HeatmapData | null>(null);
  const [compareData, setCompareData] = useState<HeatmapData | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState<string | null>(null);
  const [zoom,        setZoom]        = useState(1);
  const [tooltip,     setTooltip]     = useState<TooltipState | null>(null);

  // ── Data fetching — unchanged ────────────────────────────────────────────────
  const fetchHeatmap = useCallback(async () => {
    if (!studentId) return;
    try {
      setLoading(true);
      setError(null);

      const promises = [
        axios.get(`${API_BASE_URL}/student-mood-logs/heatmap/${studentId}`, {
          params: { period: primaryPeriod, startDate: primaryStartDate, endDate: primaryEndDate }
        }),
      ];

      if (showCompare && comparePeriod) {
        promises.push(
          axios.get(`${API_BASE_URL}/student-mood-logs/heatmap/${studentId}`, {
            params: { period: comparePeriod, startDate: compareStartDate, endDate: compareEndDate }
          })
        );
      }

      const [primaryRes, compareRes] = await Promise.all(promises);

      const raw = primaryRes.data;
      if (raw?.items) raw.items.forEach((item: any) => { if (item.zone) item.zone = formatZoneName(item.zone); });
      setData(raw);

      if (compareRes) {
        const cRaw = compareRes.data;
        if (cRaw?.items) cRaw.items.forEach((item: any) => { if (item.zone) item.zone = formatZoneName(item.zone); });
        setCompareData(cRaw);
      } else {
        setCompareData(null);
      }
    } catch (e: any) {
      setError(e?.response?.data?.error || "Failed to load heatmap");
    } finally {
      setLoading(false);
    }
  }, [studentId, primaryPeriod, comparePeriod, showCompare, primaryStartDate, primaryEndDate, compareStartDate, compareEndDate]);

  useEffect(() => { fetchHeatmap(); }, [fetchHeatmap]);

  // ── Derived values ───────────────────────────────────────────────────────────
  const combined    = data ? [...ZONE_ORDER, ...data.zones.map(formatZoneName)] : [];
  const uniqueZones = combined.filter((v, i, a) => a.indexOf(v) === i);
  // sortZones returns Green → Dark Red; we reverse so Dark Red is at top (worst first)
  const zones       = data ? [...sortZones(uniqueZones)].reverse() : [];
  const categories  = data?.categories ?? [];
  const maxCount    = data
    ? Math.max(1, ...zones.flatMap(z => categories.map(c => data.matrix[z]?.[c.id] ?? 0)))
    : 1;

  // Layout dimensions
  const colWidth   = Math.max(80, Math.round(96 * zoom));
  const ZONE_COL_W = 120; // wider zone label column
  const CELL_H     = 64;

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <div className="flex flex-col items-center gap-3">
        <div className="w-9 h-9 border-[3px] border-emerald-400 border-t-transparent rounded-full animate-spin" />
        <span className="text-xs text-gray-400 font-medium">Loading heatmap…</span>
      </div>
    </div>
  );

  // ── Error ────────────────────────────────────────────────────────────────────
  if (error) return (
    <div className="flex flex-col items-center justify-center py-20 gap-3">
      <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center">
        <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
        </svg>
      </div>
      <p className="text-sm font-semibold text-red-500">{error}</p>
      <button onClick={fetchHeatmap} className="text-xs text-emerald-600 font-bold hover:underline">Retry</button>
    </div>
  );

  // ── Empty ────────────────────────────────────────────────────────────────────
  if (!data || !zones.length || !categories.length) return (
    <div className="flex flex-col items-center justify-center py-24 gap-4">
      <div className="w-16 h-16 rounded-2xl bg-gray-50 border border-dashed border-gray-200 flex items-center justify-center">
        <svg className="w-7 h-7 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      </div>
      <div className="text-center">
        <p className="text-sm font-semibold text-gray-400">No mood logs found</p>
        <p className="text-xs text-gray-300 mt-1">No data available for the selected period.</p>
      </div>
    </div>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="w-full relative select-none">

      {/* ── Compact Header ────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="text-sm font-bold text-gray-800 leading-tight">
            Emotional Distribution Heatmap
          </h3>
          <p className="text-[11px] text-gray-400 mt-0.5">
            Category × Zone — each cell shows number of logs
          </p>
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1 flex-shrink-0 mt-0.5">
          <button
            onClick={() => setZoom(z => Math.max(0.6, z - 0.15))}
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 text-sm font-bold leading-none transition-colors"
            title="Zoom out"
          >
            −
          </button>
          <button
            onClick={() => setZoom(z => Math.min(2.0, z + 0.15))}
            className="w-6 h-6 flex items-center justify-center rounded border border-gray-200 bg-white text-gray-500 hover:bg-gray-50 text-sm font-bold leading-none transition-colors"
            title="Zoom in"
          >
            +
          </button>
        </div>
      </div>

      {/* ── Heatmap scroll container ───────────────────────────────────────────── */}
      {/* overflow-x: auto only kicks in if screen is very narrow; otherwise fills 100% */}
      <div className="overflow-x-auto overflow-y-hidden relative">

        {/* Floating tooltip */}
        {tooltip && (
          <div
            className="pointer-events-none fixed z-50 px-3 py-2.5 rounded-xl shadow-xl text-xs font-semibold text-white"
            style={{
              background: "rgba(17,24,39,0.93)",
              left: tooltip.x + 14,
              top: tooltip.y - 50,
              whiteSpace: "nowrap",
            }}
          >
            <div className="flex items-center gap-1.5 mb-1">
              <span style={{ color: getZoneColor(tooltip.zone).color }}>{tooltip.zone}</span>
              <span className="text-gray-400">×</span>
              <span>{tooltip.cat}</span>
            </div>

            {tooltip.isCompare ? (
              <div className="flex flex-col gap-1 text-[11px] min-w-[120px]">
                <div className="flex justify-between gap-4">
                  <span className="text-gray-400">Primary:</span>
                  <span className="font-bold">{tooltip.count} logs</span>
                </div>
                <div className="flex justify-between gap-4">
                  <span className="text-gray-400">Compare:</span>
                  <span className="font-bold">{tooltip.prevCount ?? 0} logs</span>
                </div>
                <div className="flex justify-between gap-4 border-t border-gray-700/50 pt-1.5 mt-0.5">
                  <span className="text-gray-400">Change:</span>
                  <span className="font-bold">
                    {tooltip.count > (tooltip.prevCount ?? 0)
                      ? `+${tooltip.count - (tooltip.prevCount ?? 0)} logs`
                      : tooltip.count < (tooltip.prevCount ?? 0)
                        ? `${tooltip.count - (tooltip.prevCount ?? 0)} logs`
                        : "No change"}
                  </span>
                </div>
                {(tooltip.prevCount ?? 0) > 0 && tooltip.count !== (tooltip.prevCount ?? 0) && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-400">% Change:</span>
                    <span className="font-bold">
                      {(((tooltip.count - (tooltip.prevCount ?? 0)) / (tooltip.prevCount ?? 0)) * 100 > 0 ? "+" : "")}
                      {(((tooltip.count - (tooltip.prevCount ?? 0)) / (tooltip.prevCount ?? 0)) * 100).toFixed(0)}%
                    </span>
                  </div>
                )}
                {(tooltip.prevCount ?? 0) === 0 && tooltip.count > 0 && (
                  <div className="flex justify-between gap-4">
                    <span className="text-gray-400">Status:</span>
                    <span className="font-bold text-emerald-400">New</span>
                  </div>
                )}
              </div>
            ) : (
              <div>
                <span className="text-white">{tooltip.count} log{tooltip.count !== 1 ? "s" : ""}</span>
              </div>
            )}
          </div>
        )}


        {/* Grid container — fills the full available width; columns share 1fr each */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: `${ZONE_COL_W}px repeat(${categories.length}, minmax(80px, 1fr))`,
            gap: "6px",
            width: "100%",
          }}
        >
          {/* ── Column header row ──────────────────────────────────────────────── */}

          {/* Corner — empty, sticky */}
          <div
            className="sticky left-0 z-20 bg-white"
            style={{ height: 72 }}
          />

          {/* Category header cells */}
          {categories.map(cat => (
            <div
              key={cat.id}
              className="flex items-end justify-center pb-2"
              style={{ height: 72 }}
            >
              <span
                className="text-[10px] font-semibold text-gray-500 text-center leading-tight"
                style={{ wordBreak: "break-word" }}
              >
                {cat.name}
              </span>
            </div>
          ))}

          {/* ── Data rows — Dark Red at top, Green at bottom ─────────────────── */}
          {zones.map(zone => {
            const zColor    = getZoneColor(zone);
            const cellFill  = zColor.color;
            const textColor = zColor.textColor;

            return (
              <React.Fragment key={zone}>

                {/* Zone label — sticky, plain white background */}
                <div
                  className="sticky left-0 z-10 flex items-center px-3"
                  style={{
                    height: CELL_H,
                    backgroundColor: "#ffffff",
                  }}
                >
                  <span
                    className="text-[11px] font-semibold truncate"
                    style={{ color: "#374151" }}
                  >
                    {zone.replace(" Zone", "")}
                  </span>
                </div>

                {/* Data cells */}
                {categories.map(cat => {
                  const count     = data.matrix[zone]?.[cat.id] ?? 0;
                  const prevCount = showCompare && compareData ? (compareData.matrix[zone]?.[cat.id] ?? 0) : 0;
                  const opacity   = cellOpacity(count, maxCount);
                  const isEmpty   = count === 0 && (!showCompare || prevCount === 0);
                  const isClickable = count > 0 && !!onCellClick;

                  // White text on filled cells (opacity high enough); zone text color otherwise
                  const numberColor = opacity > 0.42 ? "#ffffff" : textColor;

                  return (
                    <div
                      key={cat.id}
                      className={`relative flex items-center justify-center rounded-md transition-all duration-150 ${
                        isClickable
                          ? "cursor-pointer hover:scale-[1.07] hover:shadow-md hover:z-10"
                          : "cursor-default"
                      }`}
                      style={{
                        height: CELL_H,
                        backgroundColor: isEmpty ? `${cellFill}14` : cellFill,
                        opacity: isEmpty ? 1 : opacity,
                      }}
                      onMouseEnter={e => {
                        if (count > 0 || (showCompare && prevCount > 0)) {
                          setTooltip({ x: e.clientX, y: e.clientY, zone, cat: cat.name, count, prevCount, isCompare: showCompare });
                        }
                      }}
                      onMouseMove={e => {
                        if (count > 0 || (showCompare && prevCount > 0)) {
                          setTooltip(t => t ? { ...t, x: e.clientX, y: e.clientY } : null);
                        }
                      }}
                      onMouseLeave={() => setTooltip(null)}
                      onClick={() => { if (isClickable) onCellClick!(zone, cat.id); }}
                    >
                      {isEmpty ? (
                        // Empty cell: show a subtle dash
                        <span
                          className="text-[12px]"
                          style={{ color: `${cellFill}88`, fontWeight: 500 }}
                        >
                          —
                        </span>
                      ) : showCompare ? (
                        // Compare mode: primary | compare values
                        (count > 0 || prevCount > 0) && (
                          <div className="flex flex-col items-center justify-center gap-0.5">
                            <div className="flex items-center gap-1 leading-none">
                              <span className="text-[13px] font-black" style={{ color: numberColor }}>
                                {count}
                              </span>
                              <span className="text-[10px]" style={{ color: numberColor, opacity: 0.4 }}>|</span>
                              <span className="text-[11px] font-bold" style={{ color: numberColor, opacity: 0.75 }}>
                                {prevCount}
                              </span>
                            </div>
                            <div className="text-[9px] font-bold leading-none tracking-tight">
                              {count > prevCount ? (
                                <span style={{ color: numberColor }}>↑ +{count - prevCount}</span>
                              ) : count < prevCount ? (
                                <span style={{ color: numberColor, opacity: 0.8 }}>↓ {count - prevCount}</span>
                              ) : (
                                <span style={{ color: numberColor, opacity: 0.5 }}>—</span>
                              )}
                            </div>
                          </div>
                        )
                      ) : (
                        // Normal mode: single count
                        <span
                          className="text-[13px] font-bold leading-none"
                          style={{ color: numberColor }}
                        >
                          {count}
                        </span>
                      )}
                    </div>
                  );
                })}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* ── Legend ────────────────────────────────────────────────────────────── */}
      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          {/* Reverse legend to match zone order (Dark Red → Green) */}
          {zones.map(zone => {
            const zColor  = getZoneColor(zone);
            const hasData = categories.some(c => (data.matrix[zone]?.[c.id] ?? 0) > 0);
            return (
              <div
                key={zone}
                className="flex items-center gap-1.5 px-2 py-0.5 rounded-full"
                style={{
                  background: hasData ? zColor.color + "18" : "transparent",
                  border: `1px solid ${hasData ? zColor.color + "44" : "#f3f4f6"}`,
                  opacity: hasData ? 1 : 0.45,
                }}
              >
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: zColor.color }} />
                <span className="text-[10px] text-gray-600 font-semibold">{zone.replace(" Zone", "")}</span>
                {hasData && (
                  <span className="text-[9px] font-bold ml-0.5" style={{ color: zColor.color }}>
                    {categories.reduce((a, c) => a + (data.matrix[zone]?.[c.id] ?? 0), 0)}
                  </span>
                )}
              </div>
            );
          })}

          {/* Intensity scale */}
          <div className="ml-auto flex items-center gap-1.5">
            <span className="text-[9px] text-gray-300 font-semibold">Low</span>
            <div className="flex gap-0.5">
              {[0.18, 0.36, 0.55, 0.75, 1.0].map((op, i) => (
                <div key={i} className="w-3 h-3 rounded-sm" style={{ background: `rgba(30,202,184,${op})` }} />
              ))}
            </div>
            <span className="text-[9px] text-gray-400 font-semibold">High</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoryZoneGrid;
